import { useState } from "react";
import axios from "axios";
import L from "leaflet";
import { MapContainer, TileLayer, Polygon, CircleMarker, Marker, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { toast } from "@/components/ui/sonner";
import { Save, Trash2, RefreshCw, MousePointerClick, Plus, Check, X } from "lucide-react";

const V1 = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
const CENTER = [32.525, -92.1];

const KINDS = [
  { id: "service_area", label: "Service Area", color: "#0055FF", hint: "Events must land inside at least one" },
  { id: "depot", label: "Depot", color: "#00A651", hint: "Reference zone — tracked, never breaches" },
  { id: "no_go", label: "No-Go", color: "#FF3B30", hint: "Any event inside triggers a violation" },
];
const kindMeta = (k) => KINDS.find((x) => x.id === k) || KINDS[0];

const RULE_FIELDS = [
  ["max_deviation_radius_meters", "Max deviation radius (m)", "150"],
  ["allowed_arrival_window_minutes", "Arrival window (± min)", "10"],
  ["proximity_threshold_meters", "Proximity threshold (m)", "100"],
  ["telemetry_consistency_threshold", "Telemetry threshold (0–1)", "0.8"],
  ["retention_days", "Data retention (days) — blank keeps forever", "90"],
];

const ClickHandler = ({ onAdd }) => {
  useMapEvents({
    click: (e) => onAdd([e.latlng.lat, e.latlng.lng]),
  });
  return null;
};

const Geofences = () => {
  const [apiKey, setApiKey] = useState("");
  const [rules, setRules] = useState(null);
  const [zones, setZones] = useState([]);
  const [draft, setDraft] = useState(null);
  const [editingIdx, setEditingIdx] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const headers = { "X-GeoVerify-Key": apiKey };

  const loadRules = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${V1}/rules`, { headers });
      const r = res.data.rules;
      setRules(r);
      const loaded =
        r.zones && r.zones.length
          ? r.zones
          : r.geofence_polygon
            ? [{ name: "Default Zone", kind: "service_area", polygon: r.geofence_polygon }]
            : [];
      setZones(loaded);
      setDraft(null);
      setEditingIdx(null);
      loadHooks();
      try {
        const me = await axios.get(`${V1}/me`, { headers });
        setMe(me.data);
      } catch {
        setMe(null);
      }
      toast.success(`Ruleset loaded for ${res.data.tenant_id}`);
    } catch (e) {
      toast.error(e.response?.status === 401 ? "Invalid or missing API key" : "Could not load ruleset");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { zones };
      for (const [k] of RULE_FIELDS) {
        if (rules[k] !== undefined && rules[k] !== "") payload[k] = Number(rules[k]);
      }
      await axios.put(`${V1}/rules`, payload, { headers });
      toast.success("Ruleset saved — enforced on your next verification");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const startDraft = () => {
    setEditingIdx(null);
    setDraft({ name: "", kind: "service_area", points: [] });
  };

  const addPoint = (pt) => {
    if (draft) {
      setDraft({ ...draft, points: [...draft.points, pt] });
    } else if (editingIdx !== null) {
      setZones(
        zones.map((z, j) =>
          j === editingIdx ? { ...z, polygon: [...z.polygon, { lat: pt[0], lng: pt[1] }] } : z
        )
      );
    }
  };

  const moveVertex = (zoneIdx, vertIdx, lat, lng) => {
    setZones((zs) =>
      zs.map((z, j) =>
        j === zoneIdx
          ? { ...z, polygon: z.polygon.map((p, k) => (k === vertIdx ? { lat, lng } : p)) }
          : z
      )
    );
  };

  const removeVertex = (zoneIdx, vertIdx) => {
    setZones((zs) =>
      zs.map((z, j) =>
        j === zoneIdx && z.polygon.length > 3
          ? { ...z, polygon: z.polygon.filter((_, k) => k !== vertIdx) }
          : z
      )
    );
  };

  const [hooks, setHooks] = useState(null);
  const [hookUrl, setHookUrl] = useState("");
  const [hookEvents, setHookEvents] = useState(["event_verified"]);
  const [rotated, setRotated] = useState(null);

  const HOOK_TYPES = ["event_verified", "compliance_failed", "anomaly_detected"];

  const loadHooks = async (keyOverride) => {
    try {
      const res = await axios.get(`${V1}/webhooks`, {
        headers: { "X-GeoVerify-Key": keyOverride || apiKey },
      });
      setHooks(res.data.subscriptions);
    } catch {
      setHooks(null);
    }
  };

  const registerHook = async () => {
    if (!hookUrl.trim() || !hookEvents.length) return;
    try {
      const res = await axios.post(
        `${V1}/webhooks/register`,
        { url: hookUrl.trim(), events: hookEvents },
        { headers }
      );
      setRotated({ subscription_id: res.data.subscription_id, secret: res.data.secret, fresh: true });
      setHookUrl("");
      loadHooks();
      toast.success("Webhook registered — store the signing secret");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Registration failed");
    }
  };

  const rotateHook = async (id) => {
    try {
      const res = await axios.post(`${V1}/webhooks/${id}/rotate-secret`, {}, { headers });
      setRotated({ subscription_id: id, secret: res.data.secret });
      toast.success("Secret rotated — previous stays valid for 24h");
    } catch {
      toast.error("Rotation failed");
    }
  };

  const deleteHook = async (id) => {
    await axios.delete(`${V1}/webhooks/${id}`, { headers });
    loadHooks();
  };

  const toggleHookEvent = (t) =>
    setHookEvents(hookEvents.includes(t) ? hookEvents.filter((x) => x !== t) : [...hookEvents, t]);

  const commitDraft = () => {
    if (!draft.name.trim() || draft.points.length < 3) return;
    setZones([
      ...zones,
      {
        name: draft.name.trim(),
        kind: draft.kind,
        polygon: draft.points.map(([lat, lng]) => ({ lat, lng })),
      },
    ]);
    setDraft(null);
    toast.success("Zone added — save the ruleset to enforce it");
  };

  return (
    <div data-testid="geofences-page" className="bg-background text-foreground min-h-screen">
      <Nav />
      <main className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-32 pb-24">
        <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-telemetry mb-6">
          Tenant Configuration · Compliance Engine
        </p>
        <h1 className="font-display font-black tracking-tight text-5xl lg:text-7xl leading-[0.92] mb-6">
          Zone Editor.
        </h1>
        <p className="text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed mb-10">
          Draw named zones your network operates by — service areas, depots, and no-go zones. Every
          Real Mode verification is checked against them: outside all service areas triggers{" "}
          <code className="font-mono text-sm text-anomaly">geofence_breach</code>, inside a no-go
          triggers <code className="font-mono text-sm text-anomaly">no_go_violation</code>.
        </p>

        <div className="border border-border bg-card p-5 mb-8 flex flex-wrap items-center gap-4">
          <label htmlFor="geo-api-key" className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
            X-GeoVerify-Key
          </label>
          <input
            id="geo-api-key"
            data-testid="geofence-api-key-input"
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="gvk_…"
            className="flex-1 min-w-[240px] bg-background border border-border px-4 py-2.5 font-mono text-xs focus:outline-none focus:border-telemetry transition-colors duration-200"
          />
          <button
            data-testid="load-rules-btn"
            onClick={loadRules}
            disabled={loading || !apiKey}
            className="bg-foreground text-background font-mono text-[11px] tracking-[0.2em] uppercase px-6 py-2.5 flex items-center gap-2 hover:bg-telemetry transition-colors duration-200 disabled:opacity-50"
          >
            <RefreshCw className="w-3.5 h-3.5" /> {loading ? "Loading…" : "Load Ruleset"}
          </button>
        </div>

        {me && (
          <div data-testid="tenant-usage-banner" className="border border-border bg-card px-5 py-4 mb-8">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
                {me.name} · {me.plan === "pilot" ? "PILOT PLAN" : "STANDARD"}
              </p>
              <p className="font-mono text-xs">
                {me.usage_this_month.toLocaleString()}
                {me.events_included ? ` / ${me.events_included.toLocaleString()}` : ""} events · {me.month}
              </p>
              {me.plan === "pilot" && (
                <button
                  data-testid="upgrade-plan-btn"
                  onClick={async () => {
                    const res = await axios.post(`${V1.replace("/api/v1", "/api")}/payments/checkout`, {
                      package_id: "upgrade",
                      origin_url: window.location.origin,
                      tenant_id: me.tenant_id,
                    });
                    window.location.href = res.data.checkout_url;
                  }}
                  className="font-mono text-[10px] tracking-[0.2em] uppercase bg-foreground text-background px-5 py-2.5 hover:bg-truth transition-colors duration-200"
                >
                  Upgrade — $1,499
                </button>
              )}
            </div>
            {me.events_included && (
              <>
                <div className="h-2 bg-muted w-full">
                  <div
                    data-testid="usage-bar"
                    className={`h-2 transition-all duration-500 ${me.percent_used > 80 ? "bg-anomaly" : "bg-truth"}`}
                    style={{ width: `${Math.min(me.percent_used, 100)}%` }}
                  />
                </div>
                {me.percent_used > 80 && (
                  <p data-testid="usage-warning" className="font-mono text-[10px] tracking-wider text-anomaly mt-2 uppercase">
                    {me.percent_used}% of pilot allowance used — contact GeoVerify to upgrade before verification pauses
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {rules && (
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7">
              <div className="border border-border">
                <div className="border-b border-border bg-muted/50 px-4 py-2.5 flex items-center justify-between">
                  <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground flex items-center gap-2">
                    <MousePointerClick className="w-3.5 h-3.5" />
                    {draft
                      ? `Drawing "${draft.name || "new zone"}" — click map to add vertices`
                      : editingIdx !== null
                        ? `Editing "${zones[editingIdx]?.name}" — drag vertices · click map to add · click vertex to remove`
                        : "Start a new zone, or edit an existing one in place"}
                  </p>
                  <span data-testid="zone-count" className="font-mono text-[10px] tracking-widest text-muted-foreground">
                    {zones.length} ZONE{zones.length === 1 ? "" : "S"}
                  </span>
                </div>
                <MapContainer center={CENTER} zoom={13} style={{ height: 480, width: "100%" }} data-testid="geofence-map">
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <ClickHandler onAdd={addPoint} />
                  {zones.map((z, i) => (
                    <Polygon
                      key={`${z.name}-${i}`}
                      positions={z.polygon.map((p) => [p.lat, p.lng])}
                      pathOptions={{
                        color: kindMeta(z.kind).color,
                        fillColor: kindMeta(z.kind).color,
                        fillOpacity: editingIdx === i ? 0.2 : 0.12,
                        weight: editingIdx === i ? 3 : 2,
                        dashArray: editingIdx === i ? "6 6" : null,
                      }}
                    />
                  ))}
                  {editingIdx !== null &&
                    zones[editingIdx] &&
                    zones[editingIdx].polygon.map((p, vi) => (
                      <Marker
                        key={`edit-v-${vi}`}
                        data-testid={`vertex-${vi}`}
                        position={[p.lat, p.lng]}
                        draggable
                        icon={L.divIcon({
                          className: "",
                          html: `<div style="width:14px;height:14px;background:${kindMeta(zones[editingIdx].kind).color};border:2px solid #0A0A0A;cursor:grab"></div>`,
                          iconSize: [14, 14],
                          iconAnchor: [7, 7],
                        })}
                        eventHandlers={{
                          dragend: (e) => {
                            const ll = e.target.getLatLng();
                            moveVertex(editingIdx, vi, ll.lat, ll.lng);
                          },
                          click: () => removeVertex(editingIdx, vi),
                        }}
                      />
                    ))}
                  {draft && draft.points.length >= 3 && (
                    <Polygon
                      positions={draft.points}
                      pathOptions={{ color: kindMeta(draft.kind).color, fillColor: kindMeta(draft.kind).color, fillOpacity: 0.2, weight: 2, dashArray: "6 6" }}
                    />
                  )}
                  {draft &&
                    draft.points.map((pt, i) => (
                      <CircleMarker
                        key={`d-${pt[0]}-${pt[1]}-${i}`}
                        center={pt}
                        radius={6}
                        pathOptions={{ color: "#0A0A0A", fillColor: kindMeta(draft.kind).color, fillOpacity: 1, weight: 2 }}
                        eventHandlers={{
                          click: () => setDraft({ ...draft, points: draft.points.filter((_, j) => j !== i) }),
                        }}
                      />
                    ))}
                </MapContainer>
              </div>
            </div>
            <div className="lg:col-span-5 space-y-6">
              <div className="border border-border bg-card p-6">
                <div className="flex items-center justify-between mb-5">
                  <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Zones</p>
                  {!draft && (
                    <button
                      data-testid="new-zone-btn"
                      onClick={startDraft}
                      className="font-mono text-[10px] tracking-[0.2em] uppercase border border-foreground px-4 py-2 flex items-center gap-2 hover:bg-foreground hover:text-background transition-colors duration-200"
                    >
                      <Plus className="w-3.5 h-3.5" /> New Zone
                    </button>
                  )}
                </div>
                {draft && (
                  <div data-testid="zone-draft-panel" className="border border-dashed border-telemetry p-4 mb-4">
                    <input
                      data-testid="zone-name-input"
                      value={draft.name}
                      onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                      placeholder="Zone name — e.g. North Depot"
                      className="w-full bg-background border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-foreground transition-colors duration-200 mb-3"
                    />
                    <div className="flex gap-2 mb-3">
                      {KINDS.map((k) => (
                        <button
                          key={k.id}
                          data-testid={`kind-${k.id}`}
                          onClick={() => setDraft({ ...draft, kind: k.id })}
                          className={`flex-1 font-mono text-[10px] tracking-wider uppercase px-2 py-2 border transition-colors duration-200 ${
                            draft.kind === k.id ? "text-white" : "text-muted-foreground hover:text-foreground border-border"
                          }`}
                          style={draft.kind === k.id ? { backgroundColor: k.color, borderColor: k.color } : {}}
                        >
                          {k.label}
                        </button>
                      ))}
                    </div>
                    <p className="font-mono text-[10px] text-muted-foreground tracking-wider mb-3">
                      {draft.points.length} VERTICES · {kindMeta(draft.kind).hint}
                    </p>
                    <div className="flex gap-2">
                      <button
                        data-testid="add-zone-btn"
                        onClick={commitDraft}
                        disabled={!draft.name.trim() || draft.points.length < 3}
                        className="flex-1 bg-foreground text-background font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 flex items-center justify-center gap-2 hover:bg-truth transition-colors duration-200 disabled:opacity-40"
                      >
                        <Check className="w-3.5 h-3.5" /> Add Zone
                      </button>
                      <button
                        data-testid="cancel-zone-btn"
                        onClick={() => setDraft(null)}
                        className="border border-border font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 flex items-center gap-2 hover:border-anomaly hover:text-anomaly transition-colors duration-200"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                )}
                <div className="divide-y divide-border border border-border">
                  {zones.length === 0 && (
                    <p className="px-4 py-6 font-mono text-[10px] tracking-widest text-muted-foreground text-center">
                      NO ZONES YET
                    </p>
                  )}
                  {zones.map((z, i) => (
                    <div key={`${z.name}-row-${i}`} data-testid={`zone-row-${i}`} className="flex items-center gap-3 px-4 py-3">
                      <span className="w-3 h-3 shrink-0" style={{ backgroundColor: kindMeta(z.kind).color }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{z.name}</p>
                        <p className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
                          {kindMeta(z.kind).label} · {z.polygon.length} pts
                        </p>
                      </div>
                      <button
                        data-testid={`edit-zone-${i}`}
                        onClick={() => {
                          setDraft(null);
                          setEditingIdx(editingIdx === i ? null : i);
                        }}
                        className={`font-mono text-[10px] tracking-wider uppercase border px-2.5 py-1.5 transition-colors duration-200 ${
                          editingIdx === i
                            ? "bg-foreground text-background border-foreground"
                            : "border-border text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {editingIdx === i ? "Done" : "Edit"}
                      </button>
                      <button
                        data-testid={`delete-zone-${i}`}
                        onClick={() => setZones(zones.filter((_, j) => j !== i))}
                        className="text-muted-foreground hover:text-anomaly transition-colors duration-200"
                        aria-label={`Delete zone ${z.name}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border border-border bg-card p-6">
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-5">
                  Thresholds
                </p>
                <div className="space-y-4">
                  {RULE_FIELDS.map(([key, label, ph]) => (
                    <div key={key}>
                      <label htmlFor={`rule-${key}`} className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground block mb-2">
                        {label}
                      </label>
                      <input
                        id={`rule-${key}`}
                        data-testid={`rule-${key}`}
                        type="number"
                        step="any"
                        value={rules[key] ?? ""}
                        placeholder={ph}
                        onChange={(e) => setRules({ ...rules, [key]: e.target.value })}
                        className="w-full bg-background border border-border px-4 py-2.5 font-mono text-sm focus:outline-none focus:border-foreground transition-colors duration-200"
                      />
                    </div>
                  ))}
                </div>
                <button
                  data-testid="save-rules-btn"
                  onClick={save}
                  disabled={saving}
                  className="w-full mt-6 bg-foreground text-background font-mono text-[11px] tracking-[0.2em] uppercase px-6 py-3.5 flex items-center justify-center gap-2 hover:bg-telemetry transition-colors duration-200 disabled:opacity-50"
                >
                  <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save Ruleset"}
                </button>
                <p className="font-mono text-[10px] leading-relaxed tracking-wider text-muted-foreground mt-5">
                  TIP — after saving, run a Real Mode verification in the playground with this key.
                  Events are checked against every zone you drew.
                </p>
              </div>
            </div>
          </div>
        )}

        {rules && hooks !== null && (
          <div data-testid="webhooks-section" className="mt-14 border-t border-border pt-14">
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-telemetry mb-4">
              Tenant Configuration · Event Delivery
            </p>
            <h2 className="font-display font-black tracking-tight text-4xl lg:text-5xl leading-none mb-4">
              Webhooks.
            </h2>
            <p className="text-sm text-muted-foreground max-w-xl leading-relaxed mb-8">
              Every verification pushes a signed payload to your endpoint. Deliveries are signed
              with <code className="font-mono text-xs">X-GeoVerify-Signature</code> (HMAC-SHA256)
              and retried automatically on failure.
            </p>

            <div className="border border-border bg-card p-6 mb-6">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-4">
                Register Endpoint
              </p>
              <div className="flex flex-wrap gap-3 mb-4">
                <input
                  data-testid="hook-url-input"
                  value={hookUrl}
                  onChange={(e) => setHookUrl(e.target.value)}
                  placeholder="https://your-system.example/geoverify-hook"
                  className="flex-1 min-w-[280px] bg-background border border-border px-4 py-3 font-mono text-xs focus:outline-none focus:border-foreground transition-colors duration-200"
                />
                <button
                  data-testid="register-hook-btn"
                  onClick={registerHook}
                  disabled={!hookUrl.trim() || !hookEvents.length}
                  className="bg-foreground text-background font-mono text-[11px] tracking-[0.2em] uppercase px-6 py-3 hover:bg-telemetry transition-colors duration-200 disabled:opacity-40"
                >
                  Register
                </button>
              </div>
              <div className="flex gap-2 flex-wrap">
                {HOOK_TYPES.map((t) => (
                  <button
                    key={t}
                    data-testid={`hook-event-${t}`}
                    onClick={() => toggleHookEvent(t)}
                    className={`font-mono text-[10px] tracking-wider px-3 py-1.5 border transition-colors duration-200 ${
                      hookEvents.includes(t)
                        ? "bg-telemetry text-white border-telemetry"
                        : "border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {rotated && (
              <div data-testid="hook-secret-banner" className="border border-truth bg-card p-6 mb-6">
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-truth mb-3">
                  {rotated.fresh ? "Signing secret — shown once" : `Secret rotated for ${rotated.subscription_id} — shown once`}
                </p>
                <p data-testid="hook-secret-value" className="font-mono text-sm bg-muted px-4 py-3 break-all select-all">
                  {rotated.secret}
                </p>
                {!rotated.fresh && (
                  <p className="font-mono text-[10px] tracking-wider text-muted-foreground mt-3">
                    SIGNING SWITCHED IMMEDIATELY — PREVIOUS SECRET ACCEPTED BY YOUR RECEIVER FOR 24H GRACE
                  </p>
                )}
              </div>
            )}

            <div className="border border-border bg-card divide-y divide-border">
              {hooks.length === 0 && (
                <p className="px-5 py-8 font-mono text-[10px] tracking-widest text-muted-foreground text-center">
                  NO SUBSCRIPTIONS YET
                </p>
              )}
              {hooks.map((h) => (
                <div key={h.subscription_id} data-testid={`hook-row-${h.subscription_id}`} className="flex items-center gap-4 px-5 py-4 flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <p className="font-mono text-xs truncate">{h.url}</p>
                    <p className="font-mono text-[10px] tracking-wider text-muted-foreground mt-1">
                      {h.subscription_id} · {(h.events || []).join(", ")}
                    </p>
                  </div>
                  <button
                    data-testid={`rotate-${h.subscription_id}`}
                    onClick={() => rotateHook(h.subscription_id)}
                    className="font-mono text-[10px] tracking-[0.15em] uppercase border border-border px-4 py-2 hover:border-telemetry hover:text-telemetry transition-colors duration-200"
                  >
                    Rotate Secret
                  </button>
                  <button
                    data-testid={`delete-hook-${h.subscription_id}`}
                    onClick={() => deleteHook(h.subscription_id)}
                    className="font-mono text-[10px] tracking-[0.15em] uppercase border border-border px-4 py-2 hover:border-anomaly hover:text-anomaly transition-colors duration-200"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Geofences;
